import { useMemo, useState } from 'react';

import { useEvent } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { AddNoCircle, Building, Close, Locked } from '@mint-lib/icons';
import { useProfile } from '@mint-lib/profile-helpers';
import {
  Button,
  Checkbox,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TruncateText,
  typographyVariants,
} from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';

import { useWorkspaceUsers } from '../../../hooks/useWorkspaceUsers.js';
import { FileAccessType, FileAccessUser } from '../../../types/rag.js';
import { FileAccessUserRow } from './FileAccessUserRow.jsx';

const ACCESS_TYPE_SELECT_ID = 'share-files-access-type';

const getFullName = ({ firstName, lastName, email }: FileAccessUser) =>
  [firstName, lastName].filter(Boolean).join(' ') || email;

// Workspace users carry role and status fields the access list has no use for;
// keep only what a shared user is, so nothing extra reaches the eventual API.
const toFileAccessUser = ({
  id,
  firstName,
  lastName,
  email,
  avatar,
}: FileAccessUser): FileAccessUser => ({
  id,
  firstName,
  lastName,
  email,
  avatar,
});

type Props = {
  accessType: FileAccessType;
  onAccessTypeChange: (accessType: FileAccessType) => void;
  sharedUsers: FileAccessUser[];
  onSharedUsersChange: (users: FileAccessUser[]) => void;
};

const ShareFilesSection = ({
  accessType,
  onAccessTypeChange,
  sharedUsers,
  onSharedUsersChange,
}: Props) => {
  const { t } = useTranslation('@mint-lib/ai-chat');
  const { profileService } = useProfile();
  const profileData = profileService.getProfileData();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<FileAccessUser[]>([]);

  const { users, isLoading } = useWorkspaceUsers({
    enabled: isMenuOpen,
    searchValue,
  });

  const currentUser: FileAccessUser | null = useMemo(() => {
    if (!profileData) return null;

    return {
      id: profileData.id,
      firstName: profileData.profile.firstName,
      lastName: profileData.profile.lastName,
      email: profileData.email,
      avatar: profileData.profile.avatar,
    };
  }, [profileData]);

  const workspaceName =
    profileData?.profile?.workspace?.name ??
    profileData?.profile?.company?.name ??
    '';

  const options = useMemo(() => {
    const excludedIds = new Set([
      ...sharedUsers.map((user) => user.id),
      ...(currentUser ? [currentUser.id] : []),
    ]);

    return users.filter((user) => !excludedIds.has(user.id));
  }, [users, sharedUsers, currentUser]);

  const displayValue = useMemo(
    () => selectedUsers.map(getFullName).join(', '),
    [selectedUsers],
  );

  const handleOpenChange = useEvent((open: boolean) => {
    setIsMenuOpen(open);

    if (!open) {
      setSearchValue('');
    }
  });

  const handleToggleUser = useEvent((user: FileAccessUser) => {
    setSelectedUsers((prev) =>
      prev.some((selected) => selected.id === user.id)
        ? prev.filter((selected) => selected.id !== user.id)
        : [...prev, user],
    );
  });

  const handleAddUsers = useEvent(() => {
    onSharedUsersChange([
      ...sharedUsers,
      ...selectedUsers.map(toFileAccessUser),
    ]);
    setSelectedUsers([]);
    setSearchValue('');
    setIsMenuOpen(false);
  });

  const handleRemoveUser = useEvent((userId: number) => {
    onSharedUsersChange(sharedUsers.filter((user) => user.id !== userId));
  });

  const handleAccessTypeChange = useEvent((value: string) => {
    onAccessTypeChange(value as FileAccessType);
  });

  return (
    <div className="flex flex-col gap-4">
      <p className={typographyVariants({ variant: 'h4' })}>
        {t('Share files')}
      </p>

      <div className="flex items-center gap-2">
        <div className="flex-1">
          <ComboboxInput
            popoverClassName="z-popover"
            value={displayValue}
            placeholder={t('Add users')}
            open={isMenuOpen}
            onOpenChange={handleOpenChange}
            inputProps={{ id: 'share-files-users' }}
          >
            <ComboboxMenu
              withSearch
              shouldFilter={false}
              onValueChange={setSearchValue}
              placeholder={t('Search')}
              emptyMessage={isLoading ? t('Loading') : t('No options')}
            >
              {options.map((user) => {
                const itemId = `share-files-user-${user.id}`;
                const isSelected = selectedUsers.some(
                  (selected) => selected.id === user.id,
                );

                return (
                  <ComboboxItem
                    key={user.id}
                    value={String(user.id)}
                    selected={isSelected}
                    className="flex max-w-full"
                    onPointerDown={(event) => {
                      event.preventDefault();
                      handleToggleUser(user);
                    }}
                  >
                    <Label htmlFor={itemId} className="cursor-pointer">
                      <Checkbox id={itemId} checked={isSelected} />
                      <TruncateText>
                        {t('{{fullName}} ({{email}})', {
                          fullName: getFullName(user),
                          email: user.email,
                        })}
                      </TruncateText>
                    </Label>
                  </ComboboxItem>
                );
              })}
            </ComboboxMenu>
          </ComboboxInput>
        </div>

        <Button
          variant="outlined"
          size="large"
          onClick={handleAddUsers}
          disabled={!selectedUsers.length}
        >
          <AddNoCircle />
          {t('Add')}
        </Button>
      </div>

      <div className="flex flex-col gap-1">
        <Label
          variant="label01"
          color="secondary"
          htmlFor={ACCESS_TYPE_SELECT_ID}
        >
          {t('Who has access')}
        </Label>
        <Select value={accessType} onValueChange={handleAccessTypeChange}>
          <SelectTrigger
            id={ACCESS_TYPE_SELECT_ID}
            size="medium"
            className="w-full"
          >
            <span className="flex items-center gap-2">
              {accessType === 'invited' ? <Locked /> : <Building />}
              <SelectValue />
            </span>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="invited" startAdornment={<Locked />}>
              {t('Only those invited')}
            </SelectItem>
            <SelectItem value="workspace" startAdornment={<Building />}>
              {t('All {{workspaceName}} users', { workspaceName })}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-col gap-2">
        {currentUser && (
          <FileAccessUserRow
            user={currentUser}
            label={t('{{fullName}} (you)', {
              fullName: getFullName(currentUser),
            })}
            endAdornment={
              profileData?.isOwner ? (
                <span
                  className={typographyVariants({
                    variant: 'bodyShort01',
                    color: 'secondary',
                  })}
                >
                  {t('Workspace owner')}
                </span>
              ) : null
            }
          />
        )}

        {sharedUsers.map((user) => (
          <FileAccessUserRow
            key={user.id}
            user={user}
            label={getFullName(user)}
            endAdornment={
              <Button
                icon
                size="small"
                variant="text"
                aria-label={t('Remove user')}
                onClick={() => handleRemoveUser(user.id)}
              >
                <Close />
              </Button>
            }
          />
        ))}
      </div>
    </div>
  );
};

export { ShareFilesSection };
